import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Package } from "@revenuecat/purchases-js";
import { Modal } from "@/components/Modal";
import { BrBtn } from "@/components/BrBtn";
import { BrTag } from "@/components/BrTag";
import { apiFetch, QK, useMe, pollUntilPro, offsetShadow, TYPE, type UserOut } from "@chess/shared";
import {
  getManagementUrl,
  getProOffering,
  isBillingConfigured,
  PurchaseCancelledError,
  purchasePro,
} from "@/billing/purchases";
import { useToast } from "@/components/ToastProvider";
import { useTheme } from "@/theme/ThemeProvider";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Phase = "select" | "purchasing" | "activating" | "done" | "slow";

const PRO_POINTS = [
  "Full game history",
  "Depth 22 engine analysis",
  "Unlimited custom routines",
  "Priority sync every 15 min",
];

export function UpgradeModal({ open, onClose }: Props) {
  const p = useTheme();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();

  const [phase, setPhase] = useState<Phase>("select");
  const [packages, setPackages] = useState<{ monthly: Package | null; annual: Package | null } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const isPro = (me?.effective_tier ?? "free") !== "free";
  const configured = isBillingConfigured();

  useEffect(() => {
    if (!open) {
      setPhase("select");
      setPurchaseError(null);
      return;
    }
    // The cache can lag the server (e.g. opened off a tier_required rejection
    // right after an expiry) — refresh so the pro/free branch is truthful.
    qc.invalidateQueries({ queryKey: QK.me });
    if (!configured || isPro || packages) return;
    setLoadError(null);
    getProOffering()
      .then((pkgs) => {
        if (!pkgs.monthly && !pkgs.annual) {
          setLoadError("No plans are available right now. Try again later.");
        } else {
          setPackages(pkgs);
        }
      })
      .catch(() => setLoadError("Could not load plans. Check your connection and try again."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, configured, isPro]);

  async function buy(pkg: Package) {
    setPurchaseError(null);
    setPhase("purchasing");
    try {
      await purchasePro(pkg, me?.email ?? undefined);
    } catch (err) {
      if (err instanceof PurchaseCancelledError) {
        setPhase("select");
        return;
      }
      setPurchaseError(err instanceof Error ? err.message : "Purchase failed. You were not charged twice — try again.");
      setPhase("select");
      return;
    }
    setPhase("activating");
    const activated = await pollUntilPro(async () => {
      const fresh = await apiFetch<UserOut>("/v1/me");
      qc.setQueryData(QK.me, fresh);
      return fresh.effective_tier;
    });
    setPhase(activated ? "done" : "slow");
  }

  async function openManagePortal() {
    try {
      const url = await getManagementUrl();
      if (url) window.open(url, "_blank", "noopener");
      else toast.push({ kind: "danger", text: "No subscription portal available for this account." });
    } catch {
      toast.push({ kind: "danger", text: "Could not open the subscription portal." });
    }
  }

  return (
    <Modal open={open} onClose={onClose} width={620} label="UPGRADE">
      <div style={{ padding: "28px 24px 24px" }}>
        {isPro ? (
          <CenterBlock
            title="YOU'RE PRO"
            body="Your subscription is active — every gate is open."
          >
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <BrBtn size="sm" variant="ghost" withShadow={false} onClick={openManagePortal}>
                Manage subscription →
              </BrBtn>
              <BrBtn size="sm" variant="accent" onClick={onClose}>Done</BrBtn>
            </div>
          </CenterBlock>
        ) : !configured ? (
          <CenterBlock
            title="CHECKOUT UNAVAILABLE"
            body="Billing isn't configured in this environment (VITE_REVENUECAT_WEB_KEY is missing)."
          />
        ) : phase === "activating" ? (
          <CenterBlock title="ACTIVATING…" body="Payment received. Flipping the switches — this takes a few seconds." pulse />
        ) : phase === "slow" ? (
          <CenterBlock
            title="PAYMENT RECEIVED"
            body="Activation is taking longer than usual. It will land on its own — no need to pay again. Check back in a minute."
          >
            <BrBtn size="sm" variant="accent" onClick={onClose}>Close</BrBtn>
          </CenterBlock>
        ) : phase === "done" ? (
          <CenterBlock title="WELCOME TO PRO ♛" body="Full history, depth 22, unlimited routines — all unlocked." success>
            <BrBtn size="sm" variant="accent" onClick={onClose}>Let's go →</BrBtn>
          </CenterBlock>
        ) : (
          <>
            <div style={{ fontFamily: TYPE.display, fontSize: 32, fontWeight: 800, letterSpacing: -1, textTransform: "uppercase", lineHeight: 1 }}>
              Go Pro
            </div>
            <div style={{ fontSize: 13, color: p.fgMuted, marginTop: 6, marginBottom: 18, fontWeight: 500 }}>
              One subscription, everywhere you play.
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {PRO_POINTS.map((t) => (
                <div key={t} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13.5, fontWeight: 600 }}>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      background: p.success,
                      color: p.successFg,
                      border: `2px solid ${p.border}`,
                      display: "grid",
                      placeItems: "center",
                      fontFamily: TYPE.mono,
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    ✓
                  </span>
                  {t}
                </div>
              ))}
            </div>

            {loadError ? (
              <ErrorBox text={loadError} />
            ) : !packages ? (
              <div style={{ fontFamily: TYPE.mono, fontSize: 13, fontWeight: 700, color: p.fgMuted, padding: "20px 0", letterSpacing: 0.5 }}>
                LOADING PLANS…
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: packages.monthly && packages.annual ? "1fr 1fr" : "1fr", gap: 14 }}>
                {packages.monthly && (
                  <PlanCard
                    pkg={packages.monthly}
                    per="/ month"
                    busy={phase === "purchasing"}
                    onBuy={buy}
                  />
                )}
                {packages.annual && (
                  <PlanCard
                    pkg={packages.annual}
                    per="/ year"
                    badge={yearlySavings(packages)}
                    featured
                    busy={phase === "purchasing"}
                    onBuy={buy}
                  />
                )}
              </div>
            )}

            {purchaseError && <ErrorBox text={purchaseError} style={{ marginTop: 14 }} />}

            <div style={{ textAlign: "center", marginTop: 18, fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, letterSpacing: 0.8, textTransform: "uppercase" }}>
              Cancel anytime · Secure checkout by Stripe
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function yearlySavings(pkgs: { monthly: Package | null; annual: Package | null }): string | null {
  const mo = pkgs.monthly?.webBillingProduct.price;
  const yr = pkgs.annual?.webBillingProduct.price;
  if (!mo || !yr || mo.currency !== yr.currency) return null;
  const pct = Math.round(((mo.amountMicros * 12 - yr.amountMicros) / (mo.amountMicros * 12)) * 100);
  return pct > 0 ? `SAVE ${pct}%` : null;
}

function PlanCard({
  pkg,
  per,
  badge,
  featured = false,
  busy,
  onBuy,
}: {
  pkg: Package;
  per: string;
  badge?: string | null;
  featured?: boolean;
  busy: boolean;
  onBuy: (pkg: Package) => void;
}) {
  const p = useTheme();
  const price = pkg.webBillingProduct.price.formattedPrice;
  return (
    <div
      style={{
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, featured ? 5 : 3, featured ? 5 : 3),
        background: featured ? p.fg : p.bg,
        color: featured ? p.bg : p.fg,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        position: "relative",
      }}
    >
      {badge && (
        <div style={{ position: "absolute", top: -12, right: 10 }}>
          <BrTag bg={p.danger} fg={p.dangerFg}>{badge}</BrTag>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontFamily: TYPE.display, fontSize: 36, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1 }}>{price}</span>
        <span style={{ fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, opacity: 0.7 }}>{per}</span>
      </div>
      <BrBtn size="sm" variant="accent" full disabled={busy} onClick={() => onBuy(pkg)}>
        {busy ? "Opening checkout…" : "Get Pro →"}
      </BrBtn>
    </div>
  );
}

function CenterBlock({
  title,
  body,
  children,
  success = false,
  pulse = false,
}: {
  title: string;
  body: string;
  children?: React.ReactNode;
  success?: boolean;
  pulse?: boolean;
}) {
  const p = useTheme();
  return (
    <div style={{ textAlign: "center", padding: "18px 6px" }}>
      <div
        style={{
          fontFamily: TYPE.display,
          fontSize: 30,
          fontWeight: 800,
          letterSpacing: -1,
          textTransform: "uppercase",
          display: "inline-block",
          padding: "2px 10px",
          background: success ? p.success : "transparent",
          color: success ? p.successFg : p.fg,
          border: success ? `2px solid ${p.border}` : "none",
          boxShadow: success ? offsetShadow(p, 3, 3) : "none",
          animation: pulse ? "pulse 1.2s ease-in-out infinite" : undefined,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 13.5, color: p.fgMuted, marginTop: 12, marginBottom: 18, fontWeight: 500, lineHeight: 1.5 }}>
        {body}
      </div>
      {children}
      {pulse && (
        <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
      )}
    </div>
  );
}

function ErrorBox({ text, style = {} }: { text: string; style?: React.CSSProperties }) {
  const p = useTheme();
  return (
    <div
      style={{
        background: p.danger,
        color: p.dangerFg,
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, 3, 3),
        padding: "10px 12px",
        fontSize: 13,
        fontWeight: 700,
        ...style,
      }}
    >
      {text}
    </div>
  );
}
